import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DisplayKmsKeyStatus from '../../../NewIntervention/Steps/PDSSelection/DisplayKmsKeyStatus';
import {
  RequestRow,
  configurationLabel,
  displayValue,
  formatSensorModel,
  hasContact,
  hasParent,
  hourlyIndexLabelKey
} from './requestRows';

/**
 * Détail d'une demande, révélé par le chevron des tables.
 *
 * Extrait de `RequestsTable` parce que les deux étapes — Demandes et Affectation —
 * listent le même objet métier et affichent le même détail. Ses clés i18n restent sous
 * `interventionCreation.requests.detail.*` : ce bloc nomme la demande d'intervention,
 * pas l'étape qui l'affiche.
 */

/** `labelKey` est optionnel : certaines valeurs, déjà explicites, n'ont rien à préfixer. */
type DetailEntry = { labelKey?: string; value: string };

/** Une colonne du détail : son titre, ses couples libellé/valeur. */
function DetailColumn({
  titleKey,
  entries,
  children
}: {
  titleKey: string;
  entries: DetailEntry[];
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 200 }}>
      <Typography variant="overline" color="text.secondary">
        {t(titleKey)}
      </Typography>
      {entries.map((entry) => (
        <Stack key={entry.labelKey ?? entry.value} direction="row" spacing={1}>
          {entry.labelKey !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {/* Deux-points sur le libellé et non dans la traduction : la ponctuation
                  relève du gabarit, et toutes les paires libellé/valeur la portent.
                  Espace insécable, comme le veut l'usage français : une espace ordinaire
                  laisse le deux-points passer à la ligne seul quand la valeur est longue
                  et comprime le libellé. */}
              {`${t(entry.labelKey)}\u00a0:`}
            </Typography>
          )}
          <Typography variant="body2">{entry.value}</Typography>
        </Stack>
      ))}
      {children}
    </Stack>
  );
}

/** Quatre colonnes : contact, capteur, module et qualification, comme en v1. */
export default function RequestDetail({ row }: { row: RequestRow }) {
  const { t } = useTranslation();

  const sensorEntries: DetailEntry[] = [
    {
      labelKey: 'interventionCreation.requests.detail.diameter',
      value: displayValue(row.equipment_diameter)
    },
    { labelKey: 'interventionCreation.requests.detail.sensorModel', value: formatSensorModel(row) }
  ];

  // Rien à montrer tant qu'aucun type n'est posé : la v1 laissait la cellule vide.
  const hourlyIndexKey = hourlyIndexLabelKey(row);
  const qualificationEntries: DetailEntry[] =
    row.intervention_type_code === null
      ? []
      : [
          {
            labelKey: 'interventionCreation.requests.detail.configuration',
            value: configurationLabel(row, t)
          },
          // « Activation des index horaires » se lit seul : pas de libellé à préfixer.
          ...(hourlyIndexKey === null ? [] : [{ value: t(hourlyIndexKey) }]),
          {
            labelKey: 'interventionCreation.requests.detail.cause',
            value: displayValue(row.intervention_cause)
          }
        ];

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      sx={{ py: 2, px: 2 }}
      alignItems="flex-start"
    >
      <DetailColumn
        titleKey="interventionCreation.requests.detail.contactSection"
        entries={
          hasContact(row)
            ? [
                {
                  labelKey: 'interventionCreation.requests.detail.contactName',
                  value: displayValue(row.contact_name)
                },
                {
                  labelKey: 'interventionCreation.requests.detail.contactPhone',
                  value: displayValue(row.contact_telephone)
                }
              ]
            : []
        }
      >
        {!hasContact(row) && (
          <Typography variant="body2" color="text.secondary">
            {t('interventionCreation.requests.detail.noContact')}
          </Typography>
        )}
      </DetailColumn>

      <DetailColumn
        titleKey="interventionCreation.requests.detail.sensorSection"
        entries={sensorEntries}
      />

      <DetailColumn
        titleKey="interventionCreation.requests.detail.moduleSection"
        entries={
          hasParent(row)
            ? [
                {
                  labelKey: 'interventionCreation.requests.detail.parent',
                  value: displayValue(row.equipment_parent_id)
                }
              ]
            : []
        }
      >
        {!hasParent(row) && (
          <Typography variant="body2" color="text.secondary">
            {t('interventionCreation.requests.detail.noParent')}
          </Typography>
        )}
        <DisplayKmsKeyStatus
          availableKeys={row.device_available_kms_keys_list}
          missingKeys={row.device_missing_kms_keys_list}
        />
      </DetailColumn>

      <DetailColumn
        titleKey="interventionCreation.requests.detail.qualificationSection"
        entries={qualificationEntries}
      >
        {qualificationEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('interventionCreation.requests.detail.notQualified')}
          </Typography>
        )}
        {row.intervention_instructions !== null && (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {t('interventionCreation.requests.detail.instructions')}
            </Typography>
            {/* Instructions en clair et non en infobulle comme en v1 : la place ne
                manque pas dans une ligne dépliée. */}
            <Typography variant="body2">{row.intervention_instructions}</Typography>
          </Stack>
        )}
      </DetailColumn>
    </Stack>
  );
}
